import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDateTime, formatTime, formatDate } from "@/lib/datetime";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Send, Users, Plus, AtSign, Loader2 } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "";
  return formatTime(iso);
}
function fmtDay(iso) {
  if (!iso) return "";
  return formatDate(iso);
}

export default function ChatInterno() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]); // user_ids
  const [sending, setSending] = useState(false);
  const [newDialog, setNewDialog] = useState(false);
  const [newParticipants, setNewParticipants] = useState([]);
  const [newName, setNewName] = useState("");
  const listRef = useRef(null);
  const pollRef = useRef(null);

  const loadConvs = async () => {
    try { const { data } = await api.get("/chat/conversations"); setConvs(data); }
    catch (e) { /* silencio, polling */ void e; }
  };

  const loadMembers = async () => {
    try { const { data } = await api.get("/chat/members"); setMembers(data); }
    catch (e) { void e; }
  };

  const openConv = async (c) => {
    setSelected(c);
    try {
      const { data } = await api.get(`/chat/conversations/${c.id}/messages`);
      setMsgs(data);
      await loadConvs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    loadConvs(); loadMembers();
    // Polling cada 4s
    pollRef.current = setInterval(async () => {
      await loadConvs();
      if (selected) {
        try {
          const { data } = await api.get(`/chat/conversations/${selected.id}/messages`);
          setMsgs(data);
        } catch (e) { void e; }
      }
    }, 4000);
    // Pedir permiso de notificación una vez
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    return () => clearInterval(pollRef.current);
  }, [selected?.id]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/chat/conversations/${selected.id}/messages`, { text, mentions });
      setText(""); setMentions([]);
      await openConv(selected);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const createConv = async () => {
    if (newParticipants.length === 0) return toast.error("Seleccione al menos un miembro");
    try {
      const { data } = await api.post("/chat/conversations", {
        kind: newParticipants.length > 1 ? "group" : "private",
        name: newName.trim() || null,
        participant_ids: newParticipants,
      });
      toast.success("Conversación creada");
      setNewDialog(false); setNewParticipants([]); setNewName("");
      await loadConvs();
      openConv(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const nameFor = (uid) => members.find((m) => m.id === uid)?.name || "…";

  const insertMention = (m) => {
    setText((t) => `${t}${t.endsWith(" ") || !t ? "" : " "}@${m.name} `);
    setMentions((arr) => arr.includes(m.id) ? arr : [...arr, m.id]);
  };

  return (
    <div className="space-y-6" data-testid="chat-interno-page">
      <div className="flex items-start gap-4 flex-wrap justify-between">
        <div>
          <div className="h-1 w-16 bg-brand-aqua mb-4 rounded-full" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">Chat interno</h1>
          <p className="text-stone-600 mt-2 max-w-2xl"><span>Comunicación privada del equipo por empresa. Sala principal automática, conversaciones privadas y grupales, menciones.</span></p>
        </div>
        <Button onClick={() => setNewDialog(true)} className="btn-primary" data-testid="chat-new-btn"><Plus className="h-4 w-4 mr-2" />Nueva conversación</Button>
      </div>

      <div className="card-elevated overflow-hidden grid grid-cols-1 md:grid-cols-3" style={{ minHeight: "540px" }}>
        <div className="border-r border-border overflow-y-auto max-h-[540px]" data-testid="chat-convs">
          {convs.map((c) => (
            <button key={c.id} onClick={() => openConv(c)}
              className={`w-full text-left p-4 border-b border-border hover:bg-stone-50 ${selected?.id === c.id ? "bg-brand-aqua-soft/50" : ""}`}
              data-testid={`chat-conv-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-brand-ink truncate">
                  {c.kind === "main" ? "🏠 Sala principal" : (c.name || (c.participants.filter((p) => p !== user.id).slice(0, 2).map(nameFor).join(", ")))}
                </span>
                {c.unread > 0 && <Badge className="bg-brand-aqua text-brand-ink border-brand-aqua-dark text-[10px] font-bold"><span>{c.unread}</span></Badge>}
              </div>
              <div className="text-xs text-stone-500 mt-1 line-clamp-1"><span>{c.last_message || "Sin mensajes"}</span></div>
              <div className="text-[10px] text-stone-400 mt-1 flex items-center gap-1">
                <Users className="h-3 w-3" /><span>{c.participants.length}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="md:col-span-2 flex flex-col">
          {selected ? (
            <>
              <div className="p-4 border-b border-border">
                <div className="font-semibold text-brand-ink">
                  <span>{selected.kind === "main" ? "Sala principal" : (selected.name || "Chat privado")}</span>
                </div>
                <div className="text-xs text-stone-500"><span>{selected.participants.length} participantes</span></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50/40" ref={listRef}>
                {msgs.map((m) => (
                  <div key={m.id} className={`max-w-md ${m.author_id === user.id ? "ml-auto" : "mr-auto"}`}>
                    {m.author_id !== user.id && <div className="text-[10px] text-stone-500 mb-0.5 px-2"><span>{m.author_name}</span></div>}
                    <div className={`rounded-2xl px-4 py-2 text-sm ${m.author_id === user.id ? "bg-brand-emerald text-white rounded-br-sm" : "bg-white border border-border rounded-bl-sm"}`}>
                      <span>{m.text}</span>
                    </div>
                    <div className="text-[10px] text-stone-400 mt-0.5 px-2 flex items-center gap-1"><span>{fmtDay(m.sent_at)}</span><span>·</span><span>{fmtTime(m.sent_at)} (GMT-4)</span></div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="border-t border-border p-3 space-y-2 bg-white">
                {mentions.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {mentions.map((mid) => (
                      <Badge key={mid} className="bg-brand-aqua-soft text-brand-aqua-dark text-[10px]"><AtSign className="h-3 w-3 mr-1" /><span>{nameFor(mid)}</span></Badge>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escriba un mensaje…" data-testid="chat-input" />
                  <div className="relative">
                    <Button type="button" variant="outline" size="icon" onClick={() => {
                      const m = members.find((x) => x.id !== user.id);
                      if (m) insertMention(m);
                    }} title="Mencionar"><AtSign className="h-4 w-4" /></Button>
                  </div>
                  <Button type="submit" disabled={sending || !text.trim()} className="btn-primary" data-testid="chat-send">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-400"><MessageCircle className="h-8 w-8 mr-2" /><span>Seleccione una conversación</span></div>
          )}
        </div>
      </div>

      <Dialog open={newDialog} onOpenChange={setNewDialog}>
        <DialogContent><DialogHeader><DialogTitle>Nueva conversación</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre (opcional, para grupos)</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ej: Equipo Marketing" data-testid="chat-new-name" />
            </div>
            <div>
              <Label className="text-xs">Participantes</Label>
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto border border-border rounded-lg p-2">
                {members.filter((m) => m.id !== user.id).map((m) => (
                  <label key={m.id} className="flex items-center gap-2 py-1 hover:bg-stone-50 rounded px-1 cursor-pointer" data-testid={`chat-member-${m.id}`}>
                    <input type="checkbox" checked={newParticipants.includes(m.id)}
                      onChange={(e) => setNewParticipants(e.target.checked ? [...newParticipants, m.id] : newParticipants.filter((x) => x !== m.id))} />
                    <span className="text-sm">{m.name}</span>
                    <span className="text-xs text-stone-400">· {m.role}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={createConv} className="btn-primary w-full" data-testid="chat-new-submit">Crear conversación</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
